<template>
  <h1 class="sr-only">{{ pageHeading }}</h1>
  <!-- s::PAGE -->
  <div v-if="loading" class="loading"></div>

  <div v-else-if="error" class="error">{{ error.message }}</div>

  <div v-else class="contents">
    <!-- s:: ARTICLE -->
    <div class="article">
      <div class="layout">
        <div class="box">
          <ArticleSnb/>
          <div class="content-panel">
            <!-- 지도 진입점 — 전시 목록에서만 노출한다 (이벤트·뉴스에는 좌표가 없다). -->
            <div v-if="isExhibition" class="map-entry">
              <router-link class="map-entry__link" :to="mapRoute">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 21s-7-6.3-7-11a7 7 0 1 1 14 0c0 4.7-7 11-7 11z"/>
                  <circle cx="12" cy="10" r="2.4"/>
                </svg>
                지도로 보기
              </router-link>
            </div>
            <div class="search-wrap">
              <ArticleStatusFilter
                  :selected-status="articleStatus"
                  @update:selectedStatus="handleStatusChange"
              />
              <SearchBar v-model="searchQuery" :placeholder="placeholder" @submit="onSearch"/>
            </div>
            <div v-if="items.length" class="list-type1 exhibition">
              <ul>
                <li v-for="(item, index) in items" :key="index">
                  <ArticleCard v-model:isLike="item.isLike" :item="item" :to="{ name: Detail, params: { uuid: item.uuid } }" />
                  <!--
                  <a @click="beforeNavigate({ name: Detail, params: { uuid: item.uuid } })">
                    <ArticleCard v-model:isLike="item.isLike" :item="item"  />
                  </a>
                  -->
                </li>
              </ul>
            </div>
            <NoSearchResult v-else/>
            <Pagination :current-page="currentPage" :total-pages="totalPages"
                        :visible-count="5" @goToPage="goToPage"/>
            <InfiniteScroll :on-reach-end="moreData" :current-page="currentPage"
                            :total-pages="totalPages" :is-fetching="isFetching"/>
          </div>
        </div>
      </div>
    </div>
    <!-- e:: ARTICLE -->
  </div>
  <!-- e::PAGE -->
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import router from "@/router";
import {apiClient} from "@/services/auth-header";
import ArticleSnb from "@/components/snb/ArticleSnb.vue";
import SearchBar from "@/components/ui/SearchBar.vue";
import Pagination from "@/components/ui/Pagination.vue";
import InfiniteScroll from "@/components/ui/InfiniteScroll.vue";
import ArticleCard from "@/components/article/ArticleCard.vue";
import NoSearchResult from "@/components/ui/NoSearchResult.vue";
import {ARTICLE_LIST_SORT_OPTIONS} from "@/common/common-enum";
import {useUiStore} from "@/store/useUiStore";
import ArticleStatusFilter from "@/components/article/ArticleStatusFilter.vue";
import {useDeviceStore} from "@/store/deviceStore";

const deviceStore = useDeviceStore();
const isMobile = computed(() => deviceStore.isMobile);

const uiStore = useUiStore();
const route = useRoute();
const loading = ref(true);
const error = ref(null);
const items = ref([]);
const Detail = computed(() => {
  return route.name ? route.name + 'Detail' : '';
});
const API_URL = '/v1/contents/articles/' + (route.name.toLowerCase().endsWith('s') ? route.name.toLowerCase() : route.name.toLowerCase() + 's');

// 페이지 정보
const articleStatus = ref(route.query.filter || 'ALL')
const searchQuery = ref(route.query.q || "");
const currentPage = ref(Number(route.query.page || 0));
const size = ref(20);
const totalPages = ref(0);
const isFetching = ref(false);
const selectedSort = ref({
  value: route.query.sort || ARTICLE_LIST_SORT_OPTIONS[0].value,
  direction: route.query.direction || ARTICLE_LIST_SORT_OPTIONS[0].direction
});


const placeholderMap = {
  'Exhibition': '전시명 또는 장소명',
  'Event': '행사명 또는 장소명',
  'News': '사업명 또는 기관명',
}

const placeholder = computed(() => placeholderMap[route.name] || '검색어를 입력하세요')

// 지도 진입점 (mimio#30/#33). 현재 검색어·필터를 넘겨 목록으로 되돌아올 때 맥락이 유지되게 한다.
// 페이지 주제를 나타내는 h1 (mimio#63). 이 컴포넌트가 전시·이벤트·뉴스 목록을
// 공유하므로 라우트에서 뽑는다. useSeoMeta 의 목록 프리셋과 같은 말이어야 한다.
const HEADING_BY_ROUTE = { Exhibition: '전시', Event: '이벤트', News: '뉴스' };
const pageHeading = computed(() => HEADING_BY_ROUTE[route.name] || '아티클');

const isExhibition = computed(() => route.name === 'Exhibition');
const mapRoute = computed(() => ({
  name: 'ExhibitionMap',
  query: {
    ...(searchQuery.value ? {q: searchQuery.value} : {}),
    ...(articleStatus.value && articleStatus.value !== 'ALL' ? {filter: articleStatus.value} : {}),
  },
}));

async function fetchItems(page = 0, append = false) {
  if (isFetching.value) {
    return;
  }
  isFetching.value = true;

  try {
    uiStore.showLoading();
    const response = await apiClient.get(API_URL, {
      params: {
        q: searchQuery.value,
        page: page,
        size: size.value,
        sort: selectedSort.value.value,
        direction: selectedSort.value.direction,
        filter: articleStatus.value
      }
    });

    const newData = response.data.content;

    if (append) {
      items.value.push(...newData);
    } else {
      items.value = newData;
    }

    currentPage.value = response.data.pageable.pageNumber;
    totalPages.value = response.data.totalPages;
  } catch (err) {
    error.value = err;
  } finally {
    isFetching.value = false;
    loading.value = false;
    uiStore.hideLoading();
  }
}

const beforeNavigate = async (to) => {
  // await router.push({
  //   name: route.name,
  //   query: {
  //     q: searchQuery.value,
  //     page: currentPage.value,
  //     sort: selectedSort.value.value,
  //     direction: selectedSort.value.direction,
  //     filter: articleStatus.value
  //   }
  // });
  await router.push(to);
};

async function goToPage(page = 0, append = false) {
  // 새로고침..
  await router.push({
    name: route.name,
    query: {q: searchQuery.value, page: page, sort: selectedSort.value.value, direction: selectedSort.value.direction, filter: articleStatus.value}
  });

  // 가끔에러..
  // const url = new URL(window.location.href);
  // url.searchParams.set("q", searchQuery.value);
  // url.searchParams.set("page", page);
  // url.searchParams.set("sort", selectedSort.value.value);
  // url.searchParams.set("direction", selectedSort.value.direction);
  // url.searchParams.set("filter", articleStatus.value);
  // window.history.pushState({goToPage: url.toString()}, "", url.toString());
  // await fetchItems(page, append);
}

function moreData(page = 0, append = false) {
  fetchItems(page, append);
}

function onSearch(query) {
  searchQuery.value = query;
  currentPage.value = 0;
  goToPage(0);
}

function handleStatusChange(newStatus) {
  articleStatus.value = newStatus
  goToPage(0);
}

onMounted(async () => {
  if (isMobile.value) {
    currentPage.value = 0;
  }

  await fetchItems(currentPage.value);

});

</script>

<style scoped>
/* 지도 진입점 — 기존 search-wrap(필터 ↔ 검색바 양끝 정렬)에 3번째 요소를 끼우면
   양끝 정렬이 깨지므로 별도 줄로 오른쪽에 붙인다. */
.map-entry {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

/* reset.css 가 요소 선택자로 색을 박아두므로 링크 색은 명시한다. */
.map-entry__link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 13px;
  border: 1px solid #878787;
  border-radius: 5px;
  color: #cfcfcf;
  font-size: 13px;
  letter-spacing: -0.03em;
  text-decoration: none;
  transition: all 0.3s ease;
}

.map-entry__link svg {
  width: 14px;
  height: 14px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.6;
}

.map-entry__link:hover {
  border-color: #da4c0f;
  color: #da4c0f;
}

@media (max-width: 768px) {
  .map-entry {
    justify-content: flex-end;
    margin-bottom: 12px;
  }
}
</style>
